import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Check, ChevronLeft, ChevronRight, X, Plus, Eye, Upload, Crown, Zap, Star, DollarSign, Loader2, ShieldCheck, Shield, FileText, Lock, CreditCard, AlertTriangle, Ban, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { taxFilingAPI, pricingAPI } from '@/services/api';
import { uploadFile, deleteDocument } from '@/services/uploadService';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { 
  validateSSN, 
  validateEIN, 
  validateDOB, 
  validateName, 
  validateZipCode, 
  validatePhoneNumber,
  handleNumericInput,
  handleAlphabeticInput,
  US_STATES 
} from '@/utils/validators';
import { hasSecuredSSN, getDisplaySSN, getSSNSecurityStatus } from '@/utils/ssnSecurity';

const steps = [
  { id: 1, name: 'Personal Details', description: 'Basic information' },
  { id: 2, name: 'Family Information', description: 'Spouse & dependents' },
  { id: 3, name: 'Income Details', description: 'Income sources' },
  { id: 4, name: 'Deductions', description: 'Claim deductions' },
  { id: 5, name: 'Upload Documents', description: 'Required documents' },
  { id: 6, name: 'Review & Submit', description: 'Final review' },
];

export default function TaxFilingWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const filingId = searchParams.get('id');
  const viewMode = searchParams.get('mode') === 'view';
  const initialStep = parseInt(searchParams.get('step')) || 1;

  // Filter out Review & Submit step in view mode (already submitted)
  const visibleSteps = viewMode ? steps.filter(step => step.id !== 7) : steps;
  
  // Check if status allows editing - only DRAFT or NEED_INFO are editable
  const isEditableStatus = (status) => {
    // Clients can edit in DRAFT, NEED_INFO, and SUBMITTED status
    const editableStatuses = ['DRAFT', 'draft', 'NEED_INFO', 'need_info', 'SUBMITTED', 'submitted'];
    return editableStatuses.includes(status);
  };

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [filingRecord, setFilingRecord] = useState(null);
  // Start with loading=true to prevent showing empty form while checking for existing filings
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [processingPayment, setProcessingPayment] = useState(false);
  
  const [formData, setFormData] = useState({
    // Package Selection
    selectedPackage: 'standard',
    // Step 2 - Personal Details
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    ssn: '',
    dob: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    // Clean phone number - remove +1 country code prefix and non-digits, keep last 10 digits
    phone: user?.phone ? user.phone.replace(/[^0-9]/g, '').slice(-10) : '',
    // Step 3 - Family Information
    filingStatus: '',
    hasSpouse: false,
    spouseFirstName: '',
    spouseLastName: '',
    spouseSSN: '',
    spouseDOB: '',
    spouseIncome: '',
    hasDependents: false,
    dependents: [],
    hasChildcare: false,
    childcareProvider: '',
    childcareProviderTaxId: '',
    childcareAmount: '',
    // Step 4 - Income Details
    employmentStatus: '',
    employer: '',
    w2Income: '',
    otherIncome: '',
    // Step 5 - Deductions
    mortgageInterest: '',
    studentLoanInterest: '',
    charitableDonations: '',
    medicalExpenses: '',
    // Step 6 - Documents
    documents: {},
    otherDocuments: [], // Array for multiple other documents
    // Consent flags
    documentUploadConsent: false,
    draftReviewConsent: false, // New: I have reviewed the draft
    paymentConsent: false,
  });

  // Package details for tax filing - will be populated from API
  const [packageDetails, setPackageDetails] = useState({
    basic: {
      name: 'Basic',
      price: 99,
      icon: Star,
      features: ['Simple tax return (1040)', 'W-2 income only', 'Standard deduction', 'Email support', '7-day turnaround'],
      turnaround: '7 business days'
    },
    standard: {
      name: 'Standard',
      price: 199,
      icon: Zap,
      features: ['Complex returns', 'Multiple income sources', 'Itemized deductions', 'Priority support', '5-day turnaround', 'Tax planning advice'],
      turnaround: '5 business days'
    },
    premium: {
      name: 'Premium',
      price: 349,
      icon: Crown,
      features: ['All Standard features', 'Self-employment income', 'Investment income', 'Rental income', 'Dedicated CPA', '3-day turnaround', 'Audit support'],
      turnaround: '3 business days'
    }
  });
  const [loadingPricing, setLoadingPricing] = useState(true);

  // Fetch pricing plans from API
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        setLoadingPricing(true);
        // Fetch individual tax plans (plan_type='tax', active=true, is_business_filing=false)
        const plans = await pricingAPI.getPlans('tax', true, false);
        
        if (plans && plans.length > 0) {
          // Convert API plans to packageDetails format
          const iconMap = { 'basic': Star, 'standard': Zap, 'premium': Crown, 'advanced': Crown };
          const newPackageDetails = {};
          
          // Helper to normalize plan name to key (e.g., "Basic Tax Filing" -> "basic")
          const normalizeKey = (name) => {
            const lowerName = name.toLowerCase();
            if (lowerName.includes('basic')) return 'basic';
            if (lowerName.includes('standard')) return 'standard';
            if (lowerName.includes('premium')) return 'premium';
            if (lowerName.includes('advanced')) return 'advanced';
            return lowerName.split(' ')[0]; // fallback to first word
          };
          
          plans.forEach((plan, index) => {
            const key = normalizeKey(plan.name);
            newPackageDetails[key] = {
              name: plan.name,
              price: plan.price,
              icon: iconMap[key] || Star,
              features: plan.features || [],
              turnaround: plan.turnaround || `${7 - (index * 2)} business days`,
              description: plan.description
            };
          });
          
          // Only update if we got valid plans
          if (Object.keys(newPackageDetails).length > 0) {
            setPackageDetails(newPackageDetails);
          }
        }
      } catch (error) {
        console.error('Error fetching pricing plans:', error);
        // Keep default/fallback pricing
      } finally {
        setLoadingPricing(false);
      }
    };

    fetchPricingPlans();
  }, []);

  // Helper function to get package price dynamically
  const getPackagePrice = (packageName) => {
    if (!packageName) return 0;
    const key = packageName.toLowerCase();
    return packageDetails[key]?.price || 0;
  };

  // Load existing filing - either by ID or find existing filing for current year
  useEffect(() => {
    const loadFiling = async () => {
      try {
        setLoading(true);
        console.log('[TaxFilingWizard] loadFiling called, filingId:', filingId);
        let data;
        
        if (filingId) {
          // Load specific filing by ID
          console.log('[TaxFilingWizard] Loading specific filing:', filingId);
          data = await taxFilingAPI.getFiling(filingId);
          console.log('[TaxFilingWizard] Loaded filing data:', {
            id: data?.id,
            status: data?.status,
            hasPersonalInfo: !!data?.personal_info,
            hasIncomeDetails: !!data?.income_details,
            hasDeductions: !!data?.deductions
          });
        } else {
          // No ID provided - check for existing filing for current year
          const currentYear = new Date().getFullYear().toString();
          const allFilings = await taxFilingAPI.getFilings();
          
          // Find any filing for current year (enforce one filing per year)
          const existingFiling = allFilings.find(f => 
            f.tax_year === currentYear && f.filing_type === 'individual'
          );
          
          if (existingFiling) {
            // Found existing filing - redirect to it
            if (isEditableStatus(existingFiling.status)) {
              // Editable status (DRAFT, NEED_INFO) - go to edit mode
              navigate(`/filing/start?id=${existingFiling.id}&step=${initialStep}`, { replace: true });
            } else {
              // Non-editable status - go to view mode
              navigate(`/filing/start?id=${existingFiling.id}&mode=view&step=${initialStep}`, { replace: true });
            }
            return; // Exit early, component will remount with the ID
          }
          // If no filing found for current year, continue with empty form (will create new draft on save)
        }
        
        if (data) {
          setFilingRecord(data);
          
          // If status is not editable and not in view mode, redirect to view mode
          if (!isEditableStatus(data.status) && !viewMode) {
            navigate(`/filing/start?id=${data.id}&mode=view&step=${initialStep}`, { replace: true });
            return;
          }
          
          // Populate form with existing data
          // Consolidate all form data updates into a single setState to avoid race conditions
          if (data) {
            const newFormData = {};
            
            // Load personal_info
            if (data.personal_info) {
              // Remove selectedPackage from personal_info to avoid overwriting the correct value
              const { selectedPackage: _, ...personalInfoWithoutPackage } = data.personal_info;
              
              // Ensure SSN security flags are properly loaded
              if (personalInfoWithoutPackage.ssn_token || personalInfoWithoutPackage.ssn_secured) {
                personalInfoWithoutPackage.ssn_secured = true;
                if (!personalInfoWithoutPackage.ssn_token && data.personal_info.ssn_token) {
                  personalInfoWithoutPackage.ssn_token = data.personal_info.ssn_token;
                }
                if (!personalInfoWithoutPackage.ssn || personalInfoWithoutPackage.ssn.length < 9) {
                  personalInfoWithoutPackage.ssn = 'XXX-XX-XXXX';
                }
              }
              
              // Handle spouse SSN security
              if (personalInfoWithoutPackage.spouseSSN_token || personalInfoWithoutPackage.spouseSSN_secured) {
                personalInfoWithoutPackage.spouseSSN_secured = true;
                if (!personalInfoWithoutPackage.spouseSSN || personalInfoWithoutPackage.spouseSSN.length < 9) {
                  personalInfoWithoutPackage.spouseSSN = 'XXX-XX-XXXX';
                }
              }
              
              Object.assign(newFormData, personalInfoWithoutPackage);
            }
            
            // Load selected_package from the main field
            newFormData.selectedPackage = data.selected_package || 'standard';
            
            // Load income_details
            if (data.income_details) {
              Object.assign(newFormData, data.income_details);
            }
            
            // Load deductions
            if (data.deductions) {
              const { documents, otherDocuments, ...deductionsWithoutDocs } = data.deductions;
              Object.assign(newFormData, deductionsWithoutDocs);
              if (documents) {
                newFormData.documents = documents;
              }
              if (otherDocuments) {
                newFormData.otherDocuments = otherDocuments;
              }
            }
            
            // Single setState with all loaded data
            setFormData(prev => ({ ...prev, ...newFormData }));
            
            console.log('[TaxFilingWizard] Loaded filing data:', { 
              filingId: data.id, 
              status: data.status,
              hasPersonalInfo: !!data.personal_info,
              hasIncomeDetails: !!data.income_details, 
              hasDeductions: !!data.deductions,
              loadedFields: Object.keys(newFormData)
            });
          }
          
          toast.success('Loaded existing filing');
        }
      } catch (error) {
        console.error('Error loading filing:', error);
        toast.error('Failed to load filing');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadFiling();
    }
  }, [filingId, user, navigate, initialStep]);

  // Auto-save draft every 30 seconds - but not while loading
  useEffect(() => {
    const autoSave = setInterval(() => {
      // Don't auto-save while loading data or if in view mode
      if (!loading && !viewMode && currentStep > 1 && currentStep < steps.length) {
        saveDraft(true); // Silent save
      }
    }, 30000);

    return () => clearInterval(autoSave);
  }, [formData, currentStep, loading, viewMode]);

  const saveDraft = async (silent = false) => {
    // Don't save if we're still loading data or if we have a filingId but no filingRecord yet
    if (loading || (filingId && !filingRecord)) {
      console.log('[TaxFilingWizard] Skipping save - data still loading');
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        tax_year: new Date().getFullYear().toString(),
        filing_type: 'individual',
        selected_package: formData.selectedPackage,
        personal_info: {
          selectedPackage: formData.selectedPackage,
          firstName: formData.firstName,
          lastName: formData.lastName,
          ssn: formData.ssn,
          dob: formData.dob,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          phone: formData.phone,
          filingStatus: formData.filingStatus,
          hasSpouse: formData.hasSpouse,
          spouseFirstName: formData.spouseFirstName,
          spouseLastName: formData.spouseLastName,
          spouseSSN: formData.spouseSSN,
          spouseDOB: formData.spouseDOB,
          spouseIncome: formData.spouseIncome,
          hasDependents: formData.hasDependents,
          dependents: formData.dependents,
          hasChildcare: formData.hasChildcare,
          childcareProvider: formData.childcareProvider,
          childcareProviderTaxId: formData.childcareProviderTaxId,
          childcareAmount: formData.childcareAmount,
        },
        income_details: {
          employmentStatus: formData.employmentStatus,
          employer: formData.employer,
          w2Income: formData.w2Income,
          otherIncome: formData.otherIncome,
        },
        deductions: {
          mortgageInterest: formData.mortgageInterest,
          studentLoanInterest: formData.studentLoanInterest,
          charitableDonations: formData.charitableDonations,
          medicalExpenses: formData.medicalExpenses,
          documents: formData.documents,
          otherDocuments: formData.otherDocuments, // Include other documents
        },
      };

      let currentFilingId = filingRecord?.id;

      if (currentFilingId) {
        // Update existing filing
        await taxFilingAPI.updateFiling(currentFilingId, payload);
      } else {
        // Create new filing
        const newFiling = await taxFilingAPI.createFiling(payload);
        setFilingRecord(newFiling);
        currentFilingId = newFiling.id;
        // Update URL with filing ID
        navigate(`/filing/start?id=${newFiling.id}`, { replace: true });
      }

      // Upload any pending other documents
      if (currentFilingId && formData.otherDocuments?.length > 0) {
        const updatedOtherDocs = [...formData.otherDocuments];
        let hasUpdates = false;

        for (let i = 0; i < updatedOtherDocs.length; i++) {
          const doc = updatedOtherDocs[i];
          if (doc.file && !doc.uploaded) {
            try {
              const result = await uploadFile(
                doc.file,
                'tax_filing',
                currentFilingId,
                'other',
                null
              );

              if (result.success) {
                updatedOtherDocs[i] = {
                  ...doc,
                  id: result.data.id,
                  file_url: result.data.file_url,
                  uploaded: true,
                  file: undefined // Remove file reference after upload
                };
                hasUpdates = true;
              }
            } catch (uploadError) {
              console.error('Error uploading other document:', uploadError);
            }
          }
        }

        if (hasUpdates) {
          updateFormData('otherDocuments', updatedOtherDocs);
          
          // Save the filing again with the uploaded document info
          const updatedPayload = {
            ...payload,
            deductions: {
              ...payload.deductions,
              otherDocuments: updatedOtherDocs.map(doc => ({
                id: doc.id,
                file_name: doc.file_name,
                file_url: doc.file_url,
                uploaded: doc.uploaded,
                uploaded_at: doc.uploaded_at || new Date().toISOString()
              }))
            }
          };
          await taxFilingAPI.updateFiling(currentFilingId, updatedPayload);
        }
      }
      
      if (!silent) {
        toast.success('Draft saved successfully');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      if (!silent) {
        toast.error('Failed to save draft');
      }
    } finally {
      setSaving(false);
    }
  };

  const progress = (currentStep / visibleSteps.length) * 100;

  // Redirect to dashboard if no user after 2 seconds
  useEffect(() => {
    if (!user) {
      const timer = setTimeout(() => {
        console.log('No user found, redirecting to dashboard');
        navigate('/dashboard');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Tax Filing" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const validateCurrentStep = () => {
    const errors = {};
    
    // Step 1 - Personal Details Validation (was Step 2)
    if (currentStep === 1) {
      // First Name validation
      const firstNameValidation = validateName(formData.firstName);
      if (!firstNameValidation.isValid) {
        errors.firstName = firstNameValidation.error;
      }
      
      // Last Name validation
      const lastNameValidation = validateName(formData.lastName);
      if (!lastNameValidation.isValid) {
        errors.lastName = lastNameValidation.error;
      }
      
      // SSN validation - skip if already secured
      if (!hasSecuredSSN(formData)) {
        // Only validate if SSN is not secured
        if (formData.ssn && formData.ssn.includes('XXX')) {
          // If SSN contains XXX but not marked as secured, something went wrong
          console.warn('[Validation] Masked SSN without security flags:', { 
            ssn: formData.ssn, 
            ssn_secured: formData.ssn_secured, 
            ssn_token: formData.ssn_token 
          });
          // Skip validation for masked SSN even if flags are missing
        } else {
          const ssnValidation = validateSSN(formData.ssn);
          if (!ssnValidation.isValid) {
            errors.ssn = ssnValidation.error;
          }
        }
      }
      
      // DOB validation
      const dobValidation = validateDOB(formData.dob);
      if (!dobValidation.isValid) {
        errors.dob = dobValidation.error;
      }
      
      // Address validation
      if (!formData.address || formData.address.trim() === '') {
        errors.address = 'Street address is required';
      }
      
      // City validation
      if (!formData.city || formData.city.trim() === '') {
        errors.city = 'City is required';
      }
      
      // State validation
      if (!formData.state || formData.state.trim() === '') {
        errors.state = 'State is required';
      }
      
      // Zip Code validation
      const zipValidation = validateZipCode(formData.zip);
      if (!zipValidation.isValid) {
        errors.zip = zipValidation.error;
      }
      
      // Phone Number validation (REQUIRED)
      const phoneValidation = validatePhoneNumber(formData.phone);
      if (!phoneValidation.isValid) {
        errors.phone = phoneValidation.error;
      }
    }
    
    // Step 2 - Family Information Validation (was Step 3)
    if (currentStep === 2) {
      // Spouse validation (if married)
      if (formData.hasSpouse) {
        const spouseFirstNameValidation = validateName(formData.spouseFirstName);
        if (!spouseFirstNameValidation.isValid) {
          errors.spouseFirstName = spouseFirstNameValidation.error;
        }
        
        const spouseLastNameValidation = validateName(formData.spouseLastName);
        if (!spouseLastNameValidation.isValid) {
          errors.spouseLastName = spouseLastNameValidation.error;
        }
        
        // Spouse SSN/ITIN is optional - only validate format if provided
        if (formData.spouseSSN && formData.spouseSSN.trim() !== '') {
          const spouseIsSecured = formData.spouseSSN_secured && formData.spouseSSN_token;
          if (!spouseIsSecured && !formData.spouseSSN.includes('XXX')) {
            const spouseSSNValidation = validateSSN(formData.spouseSSN);
            if (!spouseSSNValidation.isValid) {
              errors.spouseSSN = spouseSSNValidation.error;
            }
          }
        }
        
        const spouseDOBValidation = validateDOB(formData.spouseDOB);
        if (!spouseDOBValidation.isValid) {
          errors.spouseDOB = spouseDOBValidation.error;
        }
      }
      
      // Dependents validation
      formData.dependents.forEach((dependent, index) => {
        const firstNameValidation = validateName(dependent.firstName);
        if (!firstNameValidation.isValid) {
          errors[`dependent_${dependent.id}_firstName`] = firstNameValidation.error;
        }
        
        const lastNameValidation = validateName(dependent.lastName);
        if (!lastNameValidation.isValid) {
          errors[`dependent_${dependent.id}_lastName`] = lastNameValidation.error;
        }
        
        // SSN/ITIN is optional for dependents - only validate format if provided
        if (dependent.ssn && dependent.ssn.trim() !== '') {
          const dependentSSNIsSecured = dependent.ssn_secured && dependent.ssn_token;
          if (!dependentSSNIsSecured && !dependent.ssn.includes('XXX')) {
            const ssnValidation = validateSSN(dependent.ssn);
            if (!ssnValidation.isValid) {
              errors[`dependent_${dependent.id}_ssn`] = ssnValidation.error;
            }
          }
        }
        
        const dobValidation = validateDOB(dependent.dob);
        if (!dobValidation.isValid) {
          errors[`dependent_${dependent.id}_dob`] = dobValidation.error;
        }
        
        if (!dependent.relationship) {
          errors[`dependent_${dependent.id}_relationship`] = 'Relationship is required';
        }
      });
      
      // Childcare validation (if applicable)
      if (formData.hasChildcare) {
        if (!formData.childcareProvider || formData.childcareProvider.trim() === '') {
          errors.childcareProvider = 'Provider name is required';
        }
        
        const einValidation = validateEIN(formData.childcareProviderTaxId);
        if (!einValidation.isValid) {
          errors.childcareProviderTaxId = einValidation.error;
        }
        
        if (!formData.childcareAmount || formData.childcareAmount <= 0) {
          errors.childcareAmount = 'Total amount paid is required';
        }
      }
    }
    
    // Step 3 - Income Details Validation (was Step 4)
    if (currentStep === 3) {
      // Employment Status
      if (!formData.employmentStatus || formData.employmentStatus.trim() === '') {
        errors.employmentStatus = 'Employment status is required';
      }
      
      // W-2 Income (REQUIRED)
      if (!formData.w2Income || formData.w2Income === '' || formData.w2Income === null || formData.w2Income === undefined) {
        errors.w2Income = 'W-2 income is required';
      } else if (parseFloat(formData.w2Income) < 0) {
        errors.w2Income = 'Income cannot be negative';
      }
      
      // Other Income (optional but if entered, must be valid)
      if (formData.otherIncome && formData.otherIncome !== '' && parseFloat(formData.otherIncome) < 0) {
        errors.otherIncome = 'Income cannot be negative';
      }
    }
    
    // Step 4 - Deductions Validation (was Step 5)
    if (currentStep === 4) {
      // Validate all deduction fields are not negative if entered
      const deductionFields = ['mortgageInterest', 'studentLoanInterest', 'charitableDonations', 'medicalExpenses'];
      
      deductionFields.forEach(field => {
        if (formData[field] && formData[field] !== '' && parseFloat(formData[field]) < 0) {
          errors[field] = 'Amount cannot be negative';
        }
      });
    }
    
    // Step 5 - Document Upload Validation (was Step 6)
    if (currentStep === 5) {
      // Require consent checkbox before proceeding
      if (!formData.documentUploadConsent) {
        errors.documentUploadConsent = 'Please accept the document upload consent to continue';
      }
    }
    
    // Step 6 - Review & Submit Validation (no validation needed - just review)
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    // Validate current step before proceeding
    if (!validateCurrentStep()) {
      toast.error('Please fix all validation errors before proceeding');
      window.scrollTo(0, 0);
      return;
    }
    
    if (currentStep < visibleSteps.length) {
      // Save draft before moving to next step
      await saveDraft(true);
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      // Final submission
      await handleSubmit();
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      
      // Save final data
      await saveDraft(true);
      
      // Update status to submitted
      if (filingRecord?.id) {
        await taxFilingAPI.updateFiling(filingRecord.id, { status: 'SUBMITTED' });
        toast.success('Tax filing submitted successfully!');
        navigate('/filing-status');
      } else {
        toast.error('Please save your filing first');
      }
    } catch (error) {
      console.error('Error submitting filing:', error);
      toast.error('Failed to submit filing');
    } finally {
      setSaving(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  // Handle Pay Now button click
  const handlePayNow = async () => {
    if (!filingRecord || !filingRecord.id) {
      toast.error('No filing found for payment');
      return;
    }

    try {
      setProcessingPayment(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const originUrl = window.location.origin;

      const response = await axios.post(
        `${backendUrl}/api/payments/create-checkout`,
        {
          filing_id: filingRecord.id,
          filing_type: 'tax',
          origin_url: originUrl
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const updateFormData = (field, value) => {
    if (viewMode) return; // Prevent updates in view mode
    console.log(`Updating ${field} to:`, value);
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      console.log('Updated formData:', updated);
      return updated;
    });
  };

  const addDependent = () => {
    const newDependent = {
      id: Date.now(),
      firstName: '',
      lastName: '',
      ssn: '',
      dob: '',
      relationship: '',
    };
    setFormData({
      ...formData,
      dependents: [...formData.dependents, newDependent]
    });
  };

  const updateDependent = (id, field, value) => {
    const updatedDependents = formData.dependents.map(dep =>
      dep.id === id ? { ...dep, [field]: value } : dep
    );
    setFormData({ ...formData, dependents: updatedDependents });
  };

  const removeDependent = (id) => {
    const updatedDependents = formData.dependents.filter(dep => dep.id !== id);
    
    // Reset childcare expenses if no dependents remain
    if (updatedDependents.length === 0) {
      setFormData({ 
        ...formData, 
        dependents: updatedDependents,
        hasChildcare: false,
        childcareProvider: '',
        childcareProviderTaxId: '',
        childcareAmount: ''
      });
    } else {
      setFormData({ ...formData, dependents: updatedDependents });
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // Package Selection Step
        return (
          // Step 1 - Personal Details (previously Step 2)
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => updateFormData('firstName', handleAlphabeticInput(e.target.value))}
                  placeholder="John"
                  required
                  disabled={viewMode}
                />
                {validationErrors.firstName && (
                  <p className="text-xs text-red-500">{validationErrors.firstName}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => updateFormData('lastName', handleAlphabeticInput(e.target.value))}
                  placeholder="Doe"
                  required
                  disabled={viewMode}
                />
                {validationErrors.lastName && (
                  <p className="text-xs text-red-500">{validationErrors.lastName}</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ssn">Social Security Number *</Label>
                <div className="relative">
                  <Input
                    id="ssn"
                    value={hasSecuredSSN(formData) ? getDisplaySSN(formData) : formData.ssn}
                    onChange={(e) => updateFormData('ssn', handleNumericInput(e.target.value, 9))}
                    placeholder="123456789"
                    maxLength={9}
                    required
                    disabled={viewMode || hasSecuredSSN(formData)}
                    className={hasSecuredSSN(formData) ? "pr-10 bg-green-50 border-green-200" : ""}
                  />
                  {hasSecuredSSN(formData) && (
                    <ShieldCheck className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-600" />
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {hasSecuredSSN(formData) ? (
                    <>
                      <ShieldCheck className="h-3 w-3 text-green-600" />
                      <span className="text-green-600">SSN encrypted & secured (AES-256)</span>
                    </>
                  ) : (
                    <>
                      <Shield className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground">9 digits, numbers only - will be encrypted on save</span>
                    </>
                  )}
                </div>
                {validationErrors.ssn && (
                  <p className="text-xs text-red-500">{validationErrors.ssn}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth *</Label>
                <Input
                  id="dob"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => updateFormData('dob', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  disabled={viewMode}
                />
                {validationErrors.dob && (
                  <p className="text-xs text-red-500">{validationErrors.dob}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Street Address *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => updateFormData('address', e.target.value)}
                placeholder="123 Main St"
                required
                disabled={viewMode}
              />
              {validationErrors.address && (
                <p className="text-xs text-red-500">{validationErrors.address}</p>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => updateFormData('city', e.target.value)}
                  placeholder="New York"
                  required
                  disabled={viewMode}
                />
                {validationErrors.city && (
                  <p className="text-xs text-red-500">{validationErrors.city}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <Select 
                  value={formData.state} 
                  onValueChange={(value) => updateFormData('state', value)}
                  disabled={viewMode}
                >
                  <SelectTrigger id="state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {US_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.state && (
                  <p className="text-xs text-red-500">{validationErrors.state}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip">ZIP Code *</Label>
                <Input
                  id="zip"
                  value={formData.zip}
                  onChange={(e) => updateFormData('zip', handleNumericInput(e.target.value, 5))}
                  placeholder="10001"
                  maxLength={5}
                  required
                  disabled={viewMode}
                />
                <p className="text-xs text-muted-foreground">5 digits</p>
                {validationErrors.zip && (
                  <p className="text-xs text-red-500">{validationErrors.zip}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => updateFormData('phone', handleNumericInput(e.target.value, 10))}
                placeholder="5551234567"
                maxLength={10}
                required
                disabled={viewMode}
              />
              <p className="text-xs text-muted-foreground">10 digits, numbers only</p>
              {validationErrors.phone && (
                <p className="text-xs text-red-500">{validationErrors.phone}</p>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {/* Filing Status */}
            <div className="space-y-2">
              <Label htmlFor="filingStatus">Filing Status *</Label>
              <Select 
                key={`filing-status-${formData.filingStatus || 'empty'}`}
                value={formData.filingStatus || undefined} 
                onValueChange={(value) => {
                  updateFormData('filingStatus', value);
                  // Auto-set hasSpouse based on filing status
                  const marriedStatuses = ['married_joint', 'married_separate'];
                  updateFormData('hasSpouse', marriedStatuses.includes(value));
                }}
              >
                <SelectTrigger id="filingStatus">
                  <SelectValue placeholder="Select filing status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="married_joint">Married Filing Jointly</SelectItem>
                  <SelectItem value="married_separate">Married Filing Separately</SelectItem>
                  <SelectItem value="head_of_household">Head of Household</SelectItem>
                  <SelectItem value="qualifying_widow">Qualifying Widow(er)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Spouse Information */}
            {(formData.filingStatus === 'married_joint' || formData.filingStatus === 'married_separate') && (
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="text-base">Spouse Information</CardTitle>
                  <CardDescription>Required for married filing status</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="spouseFirstName">Spouse First Name *</Label>
                      <Input
                        id="spouseFirstName"
                        value={formData.spouseFirstName}
                        onChange={(e) => updateFormData('spouseFirstName', handleAlphabeticInput(e.target.value))}
                        placeholder="Jane"
                        required={formData.hasSpouse}
                        disabled={viewMode}
                      />
                      {validationErrors.spouseFirstName && (
                        <p className="text-xs text-red-500">{validationErrors.spouseFirstName}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="spouseLastName">Spouse Last Name *</Label>
                      <Input
                        id="spouseLastName"
                        value={formData.spouseLastName}
                        onChange={(e) => updateFormData('spouseLastName', handleAlphabeticInput(e.target.value))}
                        placeholder="Doe"
                        required={formData.hasSpouse}
                        disabled={viewMode}
                      />
                      {validationErrors.spouseLastName && (
                        <p className="text-xs text-red-500">{validationErrors.spouseLastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="spouseSSN">Spouse SSN/ITIN</Label>
                      <Input
                        id="spouseSSN"
                        value={formData.spouseSSN}
                        onChange={(e) => updateFormData('spouseSSN', handleNumericInput(e.target.value, 9))}
                        placeholder="123456789 (Optional)"
                        maxLength={9}
                        disabled={viewMode}
                      />
                      <p className="text-xs text-muted-foreground">9 digits (optional)</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="spouseDOB">Spouse Date of Birth *</Label>
                      <Input
                        id="spouseDOB"
                        type="date"
                        value={formData.spouseDOB}
                        onChange={(e) => updateFormData('spouseDOB', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        required={formData.hasSpouse}
                        disabled={viewMode}
                      />
                      {validationErrors.spouseDOB && (
                        <p className="text-xs text-red-500">{validationErrors.spouseDOB}</p>
                      )}
                    </div>
                  </div>

                  {formData.filingStatus === 'married_joint' && (
                    <div className="space-y-2">
                      <Label htmlFor="spouseIncome">Spouse Income (if applicable)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          id="spouseIncome"
                          type="number"
                          value={formData.spouseIncome}
                          onChange={(e) => updateFormData('spouseIncome', e.target.value)}
                          placeholder="50000"
                          className="pl-7"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Dependents Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Dependents</Label>
                  <p className="text-sm text-muted-foreground">Add children or other dependents you&apos;re claiming</p>
                </div>
                <Button type="button" onClick={addDependent} variant="outline" size="sm">
                  + Add Dependent
                </Button>
              </div>

              {formData.dependents.length > 0 && (
                <div className="space-y-4">
                  {formData.dependents.map((dependent, index) => (
                    <Card key={dependent.id} className="card-elevated">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">Dependent {index + 1}</CardTitle>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeDependent(dependent.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>First Name *</Label>
                            <Input
                              value={dependent.firstName}
                              onChange={(e) => updateDependent(dependent.id, 'firstName', handleAlphabeticInput(e.target.value))}
                              placeholder="Child's first name"
                              required
                              disabled={viewMode}
                            />
                            {validationErrors[`dependent_${dependent.id}_firstName`] && (
                              <p className="text-xs text-red-500">{validationErrors[`dependent_${dependent.id}_firstName`]}</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>Last Name *</Label>
                            <Input
                              value={dependent.lastName}
                              onChange={(e) => updateDependent(dependent.id, 'lastName', handleAlphabeticInput(e.target.value))}
                              placeholder="Child's last name"
                              required
                              disabled={viewMode}
                            />
                            {validationErrors[`dependent_${dependent.id}_lastName`] && (
                              <p className="text-xs text-red-500">{validationErrors[`dependent_${dependent.id}_lastName`]}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-3">
                          <div className="space-y-2">
                            <Label>SSN/ITIN</Label>
                            <Input
                              value={dependent.ssn}
                              onChange={(e) => updateDependent(dependent.id, 'ssn', handleNumericInput(e.target.value, 9))}
                              placeholder="123456789 (Optional)"
                              maxLength={9}
                              disabled={viewMode}
                            />
                            <p className="text-xs text-muted-foreground">9 digits (optional)</p>
                          </div>
                          <div className="space-y-2">
                            <Label>Date of Birth *</Label>
                            <Input
                              type="date"
                              value={dependent.dob}
                              onChange={(e) => updateDependent(dependent.id, 'dob', e.target.value)}
                              max={new Date().toISOString().split('T')[0]}
                              required
                              disabled={viewMode}
                            />
                            {validationErrors[`dependent_${dependent.id}_dob`] && (
                              <p className="text-xs text-red-500">{validationErrors[`dependent_${dependent.id}_dob`]}</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>Relationship *</Label>
                            <Select 
                              value={dependent.relationship} 
                              onValueChange={(value) => updateDependent(dependent.id, 'relationship', value)}
                              disabled={viewMode}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="son">Son</SelectItem>
                                <SelectItem value="daughter">Daughter</SelectItem>
                                <SelectItem value="stepchild">Stepchild</SelectItem>
                                <SelectItem value="foster_child">Foster Child</SelectItem>
                                <SelectItem value="parent">Parent</SelectItem>
                                <SelectItem value="other">Other Relative</SelectItem>
                              </SelectContent>
                            </Select>
                            {validationErrors[`dependent_${dependent.id}_relationship`] && (
                              <p className="text-xs text-red-500">{validationErrors[`dependent_${dependent.id}_relationship`]}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Childcare Section */}
            {formData.dependents.length > 0 && (
              <Card className="border-secondary/20 bg-secondary/5">
                <CardHeader>
                  <CardTitle className="text-base">Childcare Expenses</CardTitle>
                  <CardDescription>If you paid for childcare to work or look for work</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="hasChildcare"
                      checked={formData.hasChildcare}
                      onChange={(e) => updateFormData('hasChildcare', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="hasChildcare" className="cursor-pointer">
                      I paid for childcare expenses
                    </Label>
                  </div>

                  {formData.hasChildcare && (
                    <>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="childcareProvider">Provider Name/Facility *</Label>
                          <Input
                            id="childcareProvider"
                            value={formData.childcareProvider}
                            onChange={(e) => updateFormData('childcareProvider', e.target.value)}
                            placeholder="ABC Daycare Center"
                            required={formData.hasChildcare}
                            disabled={viewMode}
                          />
                          {validationErrors.childcareProvider && (
                            <p className="text-xs text-red-500">{validationErrors.childcareProvider}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="childcareProviderTaxId">Provider Tax ID (EIN) *</Label>
                          <Input
                            id="childcareProviderTaxId"
                            value={formData.childcareProviderTaxId}
                            onChange={(e) => updateFormData('childcareProviderTaxId', handleNumericInput(e.target.value, 9))}
                            placeholder="123456789"
                            maxLength={9}
                            required={formData.hasChildcare}
                            disabled={viewMode}
                          />
                          <p className="text-xs text-muted-foreground">9 digits, numbers only</p>
                          {validationErrors.childcareProviderTaxId && (
                            <p className="text-xs text-red-500">{validationErrors.childcareProviderTaxId}</p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="childcareAmount">Total Childcare Amount Paid *</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                          <Input
                            id="childcareAmount"
                            type="number"
                            value={formData.childcareAmount}
                            onChange={(e) => updateFormData('childcareAmount', e.target.value)}
                            placeholder="5000"
                            className="pl-7"
                            required={formData.hasChildcare}
                            disabled={viewMode}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Enter total amount paid for childcare during the tax year
                        </p>
                        {validationErrors.childcareAmount && (
                          <p className="text-xs text-red-500">{validationErrors.childcareAmount}</p>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="employmentStatus">Employment Status *</Label>
              <Select 
                value={formData.employmentStatus} 
                onValueChange={(value) => updateFormData('employmentStatus', value)}
                disabled={viewMode}
              >
                <SelectTrigger id="employmentStatus">
                  <SelectValue placeholder="Select employment status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employed">Employed</SelectItem>
                  <SelectItem value="self-employed">Self-Employed</SelectItem>
                  <SelectItem value="unemployed">Unemployed</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
              {validationErrors.employmentStatus && (
                <p className="text-xs text-red-500">{validationErrors.employmentStatus}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="employer">Employer Name</Label>
              <Input
                id="employer"
                value={formData.employer}
                onChange={(e) => updateFormData('employer', e.target.value)}
                placeholder="ABC Corporation"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="w2Income">W-2 Income *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="w2Income"
                  type="number"
                  value={formData.w2Income}
                  onChange={(e) => updateFormData('w2Income', e.target.value)}
                  placeholder="75000"
                  className="pl-7"
                  min="0"
                  required
                  disabled={viewMode}
                />
              </div>
              {validationErrors.w2Income && (
                <p className="text-xs text-red-500">{validationErrors.w2Income}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="otherIncome">Other Income</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="otherIncome"
                  type="number"
                  value={formData.otherIncome}
                  onChange={(e) => updateFormData('otherIncome', e.target.value)}
                  placeholder="5000"
                  className="pl-7"
                  min="0"
                  disabled={viewMode}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Include interest, dividends, capital gains, etc.
              </p>
              {validationErrors.otherIncome && (
                <p className="text-xs text-red-500">{validationErrors.otherIncome}</p>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Enter your deductible expenses to potentially increase your refund.
            </p>

            <div className="space-y-2">
              <Label htmlFor="mortgageInterest">Mortgage Interest</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="mortgageInterest"
                  type="number"
                  value={formData.mortgageInterest}
                  onChange={(e) => updateFormData('mortgageInterest', e.target.value)}
                  placeholder="12000"
                  className="pl-7"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="studentLoanInterest">Student Loan Interest</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="studentLoanInterest"
                  type="number"
                  value={formData.studentLoanInterest}
                  onChange={(e) => updateFormData('studentLoanInterest', e.target.value)}
                  placeholder="2500"
                  className="pl-7"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="charitableDonations">Charitable Donations</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="charitableDonations"
                  type="number"
                  value={formData.charitableDonations}
                  onChange={(e) => updateFormData('charitableDonations', e.target.value)}
                  placeholder="1000"
                  className="pl-7"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="medicalExpenses">Medical Expenses</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="medicalExpenses"
                  type="number"
                  value={formData.medicalExpenses}
                  onChange={(e) => updateFormData('medicalExpenses', e.target.value)}
                  placeholder="3000"
                  className="pl-7"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Only amounts exceeding 7.5% of your adjusted gross income
              </p>
            </div>

            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <p className="text-sm font-medium text-foreground">
                  Estimated Total Deductions: ${(
                    Number(formData.mortgageInterest || 0) +
                    Number(formData.studentLoanInterest || 0) +
                    Number(formData.charitableDonations || 0) +
                    Number(formData.medicalExpenses || 0)
                  ).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 5:
        // Check if filing is submitted (read-only mode)
        const isSubmitted = filingRecord?.status === 'submitted' || filingRecord?.status === 'completed';
        
        const handleViewDocument = async (docId, fileUrl, fileName) => {
          try {
            const backendUrl = getBackendUrl();
            const token = localStorage.getItem('access_token');
            
            // Use the document ID endpoint if we have an ID
            if (docId) {
              const response = await fetch(`${backendUrl}/api/files/view/${docId}`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              
              if (!response.ok) {
                throw new Error('Failed to load document');
              }
              
              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);
              window.open(blobUrl, '_blank');
              return;
            }
            
            // Fallback: use view-by-url for files without ID
            if (fileUrl && fileUrl.startsWith('http')) {
              const response = await fetch(`${backendUrl}/api/files/view-by-url`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                  file_url: fileUrl,
                  entity_type: 'tax_filing',
                  entity_id: filingId
                })
              });
              
              if (!response.ok) {
                throw new Error('Failed to load document');
              }
              
              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);
              window.open(blobUrl, '_blank');
            } else {
              toast.error('Document not available');
            }
          } catch (error) {
            console.error('Error viewing document:', error);
            toast.error('Failed to load document');
          }
        };
        
        const handleFileUpload = (docType) => async (event) => {
          const file = event.target.files[0];
          if (!file) return;

          // Prevent upload if already submitted
          if (isSubmitted) {
            toast.error('Cannot upload documents to a submitted filing');
            return;
          }

          // Validate file type
          const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
          if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload PDF, JPG, or PNG files only');
            return;
          }

          // Validate file size (max 10MB)
          if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
          }

          try {
            // If no filing ID yet, create the draft first
            let uploadFilingId = filingRecord?.id || filingId;
            
            if (!uploadFilingId) {
              toast.info('Creating draft to save document...');
              // Create draft
              const payload = {
                tax_year: new Date().getFullYear().toString(),
                filing_type: 'individual',
                selected_package: formData.selectedPackage,
                personal_info: {
                  selectedPackage: formData.selectedPackage,
                  firstName: formData.firstName,
                  lastName: formData.lastName,
                  ssn: formData.ssn,
                  dob: formData.dob,
                  address: formData.address,
                  city: formData.city,
                  state: formData.state,
                  zip: formData.zip,
                  phone: formData.phone,
                  filingStatus: formData.filingStatus,
                  hasSpouse: formData.hasSpouse,
                  spouseFirstName: formData.spouseFirstName,
                  spouseLastName: formData.spouseLastName,
                  spouseSSN: formData.spouseSSN,
                  spouseDOB: formData.spouseDOB,
                  spouseIncome: formData.spouseIncome,
                  hasDependents: formData.hasDependents,
                  dependents: formData.dependents,
                },
                income_details: {
                  employmentStatus: formData.employmentStatus,
                  employer: formData.employer,
                  w2Income: formData.w2Income,
                  otherIncome: formData.otherIncome,
                },
                deductions: {
                  mortgageInterest: formData.mortgageInterest,
                  studentLoanInterest: formData.studentLoanInterest,
                  charitableDonations: formData.charitableDonations,
                  medicalExpenses: formData.medicalExpenses,
                },
              };
              
              const newFiling = await taxFilingAPI.createFiling(payload);
              setFilingRecord(newFiling);
              uploadFilingId = newFiling.id;
              navigate(`/client/tax-filing?id=${newFiling.id}`, { replace: true });
            }

            // Map frontend document types to backend enum values
            const docTypeMap = {
              'w2': 'w2',
              'form1099': '1099',
              'receipts': 'receipt'
            };

            const result = await uploadFile(
              file,
              'tax_filing',
              uploadFilingId,
              docTypeMap[docType] || docType,
              (progress) => {
                console.log(`Upload progress: ${progress}%`);
              }
            );

            if (!result.success) {
              throw new Error(result.error);
            }

            // Store document info in formData
            updateFormData('documents', {
              ...formData.documents,
              [docType]: {
                id: result.data.id,
                file_name: result.data.file_name,
                file_url: result.data.file_url
              }
            });

            toast.success(`${file.name} uploaded successfully`);
          } catch (error) {
            console.error('File upload error:', error);
            toast.error(error.message || 'Failed to upload file. Please try again.');
          }
        };

        // Handle document delete
        const handleDeleteDocument = async (docType) => {
          const doc = formData.documents?.[docType];
          if (!doc || typeof doc !== 'object' || !doc.id) {
            // Just clear local state if no server document
            updateFormData('documents', {
              ...formData.documents,
              [docType]: null
            });
            toast.success('Document removed');
            return;
          }

          try {
            const success = await deleteDocument(doc.id);
            if (success) {
              updateFormData('documents', {
                ...formData.documents,
                [docType]: null
              });
              toast.success('Document deleted successfully');
            } else {
              toast.error('Failed to delete document');
            }
          } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete document');
          }
        };

        // Handle other document delete
        const handleDeleteOtherDocument = async (index) => {
          const doc = formData.otherDocuments?.[index];
          if (!doc) return;

          try {
            if (doc.id) {
              await deleteDocument(doc.id);
            }
            const newDocs = formData.otherDocuments.filter((_, i) => i !== index);
            updateFormData('otherDocuments', newDocs);
            toast.success('Document deleted successfully');
          } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete document');
          }
        };

        return (
          <div className="space-y-6">
            {isSubmitted ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  This filing has been submitted. You can view your uploaded documents but cannot modify them.
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Upload all required tax documents. Accepted formats: PDF, JPG, PNG (Max 10MB each)
              </p>
            )}

            <div className="space-y-4">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-base">Required Documents</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="flex-1">
                      <p className="font-medium">W-2 Form</p>
                      <p className="text-sm text-muted-foreground">Wage and tax statement</p>
                      {formData.documents?.w2 && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ {typeof formData.documents.w2 === 'object' ? formData.documents.w2.file_name : formData.documents.w2}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {formData.documents?.w2 && typeof formData.documents.w2 === 'object' && formData.documents.w2.file_url && (viewMode || isSubmitted || filingId) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDocument(formData.documents.w2.id, formData.documents.w2.file_url, formData.documents.w2.file_name)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View
                        </Button>
                      )}
                      {formData.documents?.w2 && !viewMode && !isSubmitted && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteDocument('w2')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      <input
                        type="file"
                        id="w2-upload"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={handleFileUpload('w2')}
                        disabled={viewMode || isSubmitted}
                      />
                      <Button 
                        variant="outline"
                        size="sm" 
                        onClick={() => !(viewMode || isSubmitted) && document.getElementById('w2-upload').click()}
                        disabled={viewMode || isSubmitted}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {formData.documents?.w2 ? 'Replace' : 'Upload'}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="flex-1">
                      <p className="font-medium">1099 Forms</p>
                      <p className="text-sm text-muted-foreground">If applicable</p>
                      {formData.documents?.form1099 && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ {typeof formData.documents.form1099 === 'object' ? formData.documents.form1099.file_name : formData.documents.form1099}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {formData.documents?.form1099 && typeof formData.documents.form1099 === 'object' && formData.documents.form1099.file_url && (viewMode || isSubmitted || filingId) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDocument(formData.documents.form1099.id, formData.documents.form1099.file_url, formData.documents.form1099.file_name)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View
                        </Button>
                      )}
                      {formData.documents?.form1099 && !viewMode && !isSubmitted && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteDocument('form1099')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      <input
                        type="file"
                        id="1099-upload"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={handleFileUpload('form1099')}
                        disabled={viewMode || isSubmitted}
                      />
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => !(viewMode || isSubmitted) && document.getElementById('1099-upload').click()}
                        disabled={viewMode || isSubmitted}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {formData.documents?.form1099 ? 'Replace' : 'Upload'}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="flex-1">
                      <p className="font-medium">Deduction Receipts</p>
                      <p className="text-sm text-muted-foreground">Supporting documents</p>
                      {formData.documents?.receipts && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ {typeof formData.documents.receipts === 'object' ? formData.documents.receipts.file_name : formData.documents.receipts}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {formData.documents?.receipts && typeof formData.documents.receipts === 'object' && formData.documents.receipts.file_url && (viewMode || isSubmitted || filingId) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDocument(formData.documents.receipts.id, formData.documents.receipts.file_url, formData.documents.receipts.file_name)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View
                        </Button>
                      )}
                      {formData.documents?.receipts && !viewMode && !isSubmitted && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteDocument('receipts')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      <input
                        type="file"
                        id="receipts-upload"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={handleFileUpload('receipts')}
                        disabled={viewMode || isSubmitted}
                      />
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => !(viewMode || isSubmitted) && document.getElementById('receipts-upload').click()}
                        disabled={viewMode || isSubmitted}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {formData.documents?.receipts ? 'Replace' : 'Upload'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-base">Other Documents</CardTitle>
                  <CardDescription>Upload any additional supporting documents</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!isSubmitted && !viewMode && (
                    <div 
                      className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer bg-muted/30"
                      onClick={() => document.getElementById('other-docs-upload').click()}
                    >
                      <input
                        type="file"
                        id="other-docs-upload"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          if (files.length === 0) return;
                          
                          // Need a filing ID to upload
                          let currentFilingId = filingId;
                          if (!currentFilingId) {
                            try {
                              toast.info('Creating draft to save documents...');
                              const backendUrl = getBackendUrl();
                              const token = localStorage.getItem('access_token');
                              const draftResponse = await axios.post(`${backendUrl}/api/tax-filings`, {
                                tax_year: new Date().getFullYear().toString(),
                                status: 'DRAFT',
                                personal_info: formData
                              }, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              currentFilingId = draftResponse.data.id;
                              navigate(`/filing/start?id=${currentFilingId}`, { replace: true });
                            } catch (err) {
                              toast.error('Failed to create draft for upload');
                              e.target.value = '';
                              return;
                            }
                          }
                          
                          const newDocs = [...(formData.otherDocuments || [])];
                          
                          for (const file of files) {
                            if (file.size > 10 * 1024 * 1024) {
                              toast.error(`${file.name} is too large. Max size is 10MB`);
                              continue;
                            }
                            
                            try {
                              toast.info(`Uploading ${file.name}...`);
                              const result = await uploadFile(
                                file,
                                'tax_filing',
                                currentFilingId,
                                'other',
                                null
                              );
                              
                              if (result.success) {
                                newDocs.push({
                                  id: result.data.id,
                                  file_name: file.name,
                                  file_url: result.data.file_url,
                                  size: file.size,
                                  uploaded: true,
                                  uploaded_at: new Date().toISOString()
                                });
                              } else {
                                toast.error(`Failed to upload ${file.name}`);
                              }
                            } catch (uploadError) {
                              console.error('Error uploading:', uploadError);
                              toast.error(`Failed to upload ${file.name}`);
                            }
                          }
                          
                          // Update local state
                          updateFormData('otherDocuments', newDocs);
                          
                          // Save to database immediately
                          try {
                            const backendUrl = getBackendUrl();
                            const token = localStorage.getItem('access_token');
                            await axios.patch(`${backendUrl}/api/tax-filings/${currentFilingId}`, {
                              deductions: {
                                ...formData,
                                otherDocuments: newDocs.map(doc => ({
                                  id: doc.id,
                                  file_name: doc.file_name,
                                  file_url: doc.file_url,
                                  size: doc.size,
                                  uploaded: doc.uploaded,
                                  uploaded_at: doc.uploaded_at
                                }))
                              }
                            }, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success(`${files.length} file(s) uploaded and saved`);
                          } catch (saveError) {
                            console.error('Error saving documents:', saveError);
                            toast.error('Documents uploaded but failed to save. Please click Save Draft.');
                          }
                          
                          e.target.value = '';
                        }}
                        disabled={viewMode || isSubmitted}
                      />
                      <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm font-medium">Click to upload additional documents</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        You can upload multiple files (PDF, JPG, PNG - Max 10MB each)
                      </p>
                    </div>
                  )}
                  
                  {formData.otherDocuments && formData.otherDocuments.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Uploaded Documents ({formData.otherDocuments.length})</p>
                      {formData.otherDocuments.map((doc, index) => {
                        // Handle different document formats
                        const docName = doc.file_name || doc.filename || doc.name || `Document ${index + 1}`;
                        const docUrl = doc.file_url || doc.url;
                        const isUploaded = doc.uploaded || !!docUrl;
                        const docSize = doc.size || doc.file_size;
                        
                        return (
                          <div key={doc.id || index} className="flex items-center justify-between rounded-lg border p-3 bg-muted/50">
                            <div className="flex items-center gap-3 flex-1">
                              <FileText className="h-5 w-5 text-primary" />
                              <div>
                                <p className="text-sm font-medium">{docName}</p>
                                <p className="text-xs text-muted-foreground">
                                  {docSize ? `${(docSize / 1024).toFixed(1)} KB` : ''}
                                  {isUploaded ? (
                                    <span className="text-green-600 ml-2">✓ Uploaded</span>
                                  ) : (
                                    <span className="text-yellow-600 ml-2">⚠ Pending upload</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {docUrl && (viewMode || isSubmitted || filingId) && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewDocument(doc.id, docUrl, docName)}
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  View
                                </Button>
                              )}
                              {!docUrl && (viewMode || isSubmitted) && (
                                <span className="text-xs text-red-500">File missing</span>
                              )}
                              {!isSubmitted && !viewMode && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteOtherDocument(index)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    (viewMode || isSubmitted) && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No additional documents uploaded
                      </p>
                    )
                  )}
                </CardContent>
              </Card>

              {/* Security & Consent Section */}
              {!viewMode && !isSubmitted && (
                <Card className="card-elevated border-primary/20 bg-primary/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lock className="h-5 w-5 text-primary" />
                      Security & Consent
                    </CardTitle>
                    <CardDescription>
                      Your documents are protected with industry-standard security
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Security Information */}
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <ShieldCheck className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="font-medium text-green-800 dark:text-green-200 mb-2">
                            Your documents are secure
                          </p>
                          <ul className="space-y-1 text-green-700 dark:text-green-300">
                            <li>• All files are encrypted using AES-256 encryption</li>
                            <li>• Data is transmitted securely via HTTPS/TLS</li>
                            <li>• Only authorized staff can access your documents</li>
                            <li>• We never sell or share your personal data</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Consent Checkbox */}
                    <div className={cn(
                      "flex items-start space-x-3 p-3 border rounded-lg",
                      validationErrors.documentUploadConsent && "border-red-500 bg-red-50 dark:bg-red-900/20"
                    )}>
                      <Checkbox 
                        id="documentUploadConsent" 
                        checked={formData.documentUploadConsent}
                        onCheckedChange={(checked) => updateFormData('documentUploadConsent', checked)}
                        data-testid="document-consent-checkbox"
                      />
                      <div className="flex-1">
                        <label htmlFor="documentUploadConsent" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                          I confirm that I have the legal right to upload these documents and consent to Kosha Tax 
                          processing them for tax preparation purposes in accordance with the{' '}
                          <Link to="/privacy" target="_blank" className="text-primary hover:underline font-medium">
                            Privacy Policy
                          </Link>
                          {' '}and{' '}
                          <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium">
                            Terms & Conditions
                          </Link>.
                        </label>
                        {validationErrors.documentUploadConsent && (
                          <p className="text-xs text-red-500 mt-1">{validationErrors.documentUploadConsent}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Name:</span>
                  <span className="font-medium">{formData.firstName} {formData.lastName}</span>
                  <span className="text-muted-foreground">SSN:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{getDisplaySSN(formData)}</span>
                    {hasSecuredSSN(formData) ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                        <ShieldCheck className="h-3 w-3" />
                        Encrypted
                      </span>
                    ) : null}
                  </div>
                  <span className="text-muted-foreground">Address:</span>
                  <span className="font-medium">{formData.address || 'Not provided'}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>Income Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">W-2 Income:</span>
                  <span className="font-medium">${formData.w2Income || '0'}</span>
                  <span className="text-muted-foreground">Other Income:</span>
                  <span className="font-medium">${formData.otherIncome || '0'}</span>
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-bold text-primary">
                    ${(Number(formData.w2Income || 0) + Number(formData.otherIncome || 0)).toLocaleString()}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>Deductions Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Total Deductions:</span>
                  <span className="font-bold text-secondary">
                    ${(
                      Number(formData.mortgageInterest || 0) +
                      Number(formData.studentLoanInterest || 0) +
                      Number(formData.charitableDonations || 0) +
                      Number(formData.medicalExpenses || 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </CardContent>
            </Card>

          </div>
        );

      default:
        return null;
    }
  };

  return (
    <DashboardLayout role={user.role} title="Tax Filing" user={user}>
      <div className="mx-auto max-w-4xl space-y-6">
        {/* View Mode Banner with Status */}
        {viewMode && (
          <Card className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700">
            <CardContent className="py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="font-semibold text-blue-900 dark:text-blue-100">
                      Read Only Mode
                    </span>
                  </div>
                  <div className="h-6 w-px bg-blue-300 dark:bg-blue-700"></div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-blue-700 dark:text-blue-200">
                      Tax Year: <strong>{formData.taxYear || new Date().getFullYear()}</strong>
                    </span>
                    <div className="h-4 w-px bg-blue-300 dark:bg-blue-700"></div>
                    <span className="text-sm text-blue-700 dark:text-blue-200">
                      Status: <span className={cn(
                        "font-semibold uppercase px-2 py-0.5 rounded text-xs",
                        filingRecord?.status === 'draft' && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
                        filingRecord?.status === 'submitted' && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
                        filingRecord?.status === 'under_review' && "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
                        filingRecord?.status === 'completed' && "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                      )}>
                        {filingRecord?.status?.replace('_', ' ') || 'Draft'}
                      </span>
                    </span>
                    {filingRecord?.estimated_refund && (
                      <>
                        <div className="h-4 w-px bg-blue-300 dark:bg-blue-700"></div>
                        <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                          Estimated Refund: ${Number(filingRecord.estimated_refund).toLocaleString()}
                        </span>
                      </>
                    )}
                    {filingRecord?.balance_due && (
                      <>
                        <div className="h-4 w-px bg-blue-300 dark:bg-blue-700"></div>
                        <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                          Balance Due: ${Number(filingRecord.balance_due).toLocaleString()}
                        </span>
                      </>
                    )}
                    {(filingRecord?.selected_package || formData.selectedPackage) && (
                      <>
                        <div className="h-4 w-px bg-blue-300 dark:bg-blue-700"></div>
                        <span className="text-sm text-blue-700 dark:text-blue-200">
                          Package: <span className={cn(
                            "font-semibold capitalize px-2 py-0.5 rounded text-xs",
                            (filingRecord?.selected_package || formData.selectedPackage) === 'basic' && "bg-slate-100 text-slate-700",
                            (filingRecord?.selected_package || formData.selectedPackage) === 'standard' && "bg-blue-100 text-blue-700",
                            (filingRecord?.selected_package || formData.selectedPackage) === 'premium' && "bg-amber-100 text-amber-700"
                          )}>
                            {filingRecord?.selected_package || formData.selectedPackage}
                          </span>
                        </span>
                      </>
                    )}
                  </div>
                  {/* Payment Summary Section in View Mode - Only show when status is READY_FOR_REVIEW or later */}
                  {viewMode && (filingRecord?.selected_package || formData.selectedPackage) && 
                   filingRecord?.status && ['READY_FOR_REVIEW', 'SUBMITTED_TO_IRS', 'COMPLETED'].includes(filingRecord.status.toUpperCase()) && (
                    <div className="flex items-center gap-4 mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-blue-700 dark:text-blue-200">
                          Amount: <strong>${getPackagePrice(filingRecord?.selected_package || formData.selectedPackage)}</strong>
                        </span>
                        {filingRecord?.discount_amount > 0 && (
                          <>
                            <span className="text-orange-600 dark:text-orange-400">
                              Discount: <strong>-${Number(filingRecord.discount_amount).toLocaleString()}</strong>
                            </span>
                            <span className="text-green-600 dark:text-green-400 font-semibold">
                              Total: ${Math.max(0, getPackagePrice(filingRecord?.selected_package || formData.selectedPackage) - 
                                (filingRecord?.discount_amount || 0)).toLocaleString()}
                            </span>
                          </>
                        )}
                        {!filingRecord?.discount_amount && (
                          <span className="text-green-600 dark:text-green-400 font-semibold">
                            Total: ${getPackagePrice(filingRecord?.selected_package || formData.selectedPackage)}
                          </span>
                        )}
                        {/* Payment Status */}
                        {filingRecord?.payment_status === 'paid' && (
                          <span className="text-green-600 dark:text-green-400 font-semibold flex items-center gap-1 bg-green-100 dark:bg-green-900 px-2 py-0.5 rounded">
                            <DollarSign className="h-3 w-3" /> Paid
                          </span>
                        )}
                      </div>
                      {/* Only show Pay Now button if not already paid */}
                      {filingRecord?.payment_status !== 'paid' && (
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white ml-auto"
                          disabled={processingPayment}
                          onClick={handlePayNow}
                        >
                          {processingPayment ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <DollarSign className="h-4 w-4 mr-1" />
                              Pay Now
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {filingRecord && isEditableStatus(filingRecord.status) && (
                    <Button 
                      size="sm"
                      onClick={() => navigate(`/filing/start?id=${filingId}&step=${currentStep}`)}
                      className="bg-primary text-primary-foreground"
                    >
                      Continue Editing
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/dashboard')}
                    className="bg-white dark:bg-gray-800"
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Progress Bar */}
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Step {currentStep} of {steps.length}</span>
                <span className="text-muted-foreground">{Math.round(progress)}% Complete</span>
              </div>
              <Progress value={progress} className="mt-2" />
            </div>

            {/* Stepper */}
            <div className="flex items-center justify-between">
              {visibleSteps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-full border-2 font-semibold transition-colors',
                        currentStep > step.id
                          ? 'border-primary bg-primary text-primary-foreground'
                          : currentStep === step.id
                          ? 'border-primary bg-background text-primary'
                          : 'border-muted-foreground/30 bg-background text-muted-foreground',
                        viewMode && 'cursor-pointer hover:scale-110 transition-transform'
                      )}
                      onClick={() => viewMode && setCurrentStep(step.id)}
                      role={viewMode ? 'button' : undefined}
                      tabIndex={viewMode ? 0 : undefined}
                    >
                      {currentStep > step.id ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        step.id
                      )}
                    </div>
                    <div className="mt-2 hidden text-center md:block">
                      <p className="text-xs font-medium">{step.name}</p>
                      <p className="text-xs text-muted-foreground">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        'h-0.5 flex-1 transition-colors',
                        currentStep > step.id ? 'bg-primary' : 'bg-muted-foreground/30'
                      )}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Step Content */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{steps[currentStep - 1]?.name || `Step ${currentStep}`}</CardTitle>
            <CardDescription>{steps[currentStep - 1]?.description || ''}</CardDescription>
          </CardHeader>
          <CardContent className={viewMode ? 'pointer-events-none opacity-75' : ''}>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        {viewMode ? (
          <div className="flex justify-center">
            <Button onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1 || saving}
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>
            
            <div className="flex gap-3">
              {/* Only show Save Draft button for DRAFT status filings */}
              {currentStep > 1 && currentStep < visibleSteps.length && 
               (!filingRecord || filingRecord.status === 'DRAFT' || filingRecord.status === 'draft') && (
                <Button 
                  variant="outline" 
                  onClick={() => saveDraft(false)}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </Button>
              )}
              
              <Button onClick={handleNext} disabled={saving}>
                {saving ? 'Processing...' : (currentStep === visibleSteps.length ? 'Submit Filing' : 'Next')}
                {currentStep < visibleSteps.length && !saving && <ChevronRight className="ml-2 h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
